import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPaperTradingSessions, pausePaperTradingSession, resumePaperTradingSession, deletePaperTradingSession } from '../api';
import Spinner from '../components/Spinner';

function fmtCurrency(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function StatusBadge({ status }) {
  const colors = {
    active: { bg: 'rgba(47,214,195,0.15)', color: '#2fd6c3', border: 'rgba(47,214,195,0.4)' },
    paused: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'rgba(245,158,11,0.4)' },
    stopped: { bg: 'rgba(248,81,73,0.15)', color: '#f85149', border: 'rgba(248,81,73,0.4)' },
  };
  const c = colors[status] || colors.stopped;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>{status}</span>
  );
}

export default function PaperTradingPage({ onStartNew }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // session to delete
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await getPaperTradingSessions();
      setSessions(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePause = async (session) => {
    setActionLoading(session.id);
    try {
      await pausePaperTradingSession(session.id);
      await load();
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (session) => {
    setActionLoading(session.id);
    try {
      await resumePaperTradingSession(session.id);
      await load();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (session) => {
    setConfirmDelete(null);
    setActionLoading(session.id);
    try {
      await deletePaperTradingSession(session.id);
      await load();
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spinner size={32} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px' }}>

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
            padding: 28, maxWidth: 400, width: '100%',
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Delete Paper Trade?</div>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 20px', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text)' }}>{confirmDelete.name}</strong> and all its trade history will be permanently deleted. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => handleDelete(confirmDelete)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: 'rgba(248,81,73,0.15)', color: '#f85149', border: '1px solid rgba(248,81,73,0.4)',
                }}
              >
                Delete
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>Paper Trading</h1>
          <p style={{ margin: '6px 0 0', color: 'var(--muted)', fontSize: 13 }}>
            Simulate live trading with virtual capital. Runs daily at 4:30 PM ET.
          </p>
        </div>
        {onStartNew && (
          <button onClick={onStartNew} style={{
            background: 'var(--teal)', color: 'var(--bg)', border: 'none',
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
          }}>
            + New Session
          </button>
        )}
      </div>

      {sessions.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
          <h3 style={{ color: 'var(--text)', margin: '0 0 8px' }}>No paper trading sessions yet</h3>
          <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 13 }}>
            Run a backtest first, then start paper trading from the results page.
          </p>
          <button onClick={() => navigate('/backtest')} style={{
            background: 'var(--teal)', color: 'var(--bg)', border: 'none',
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>
            Run a Backtest
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sessions.map((session) => {
            const returnPct = session.total_return_pct;
            const isPositive = parseFloat(returnPct) >= 0;
            const isLoading = actionLoading === session.id;

            return (
              <div
                key={session.id}
                style={{
                  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
                  padding: 24, cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onClick={() => navigate(`/paper-trading/${session.id}`)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--teal)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                      {session.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Started: {new Date(session.started_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      {session.last_run_at && (
                        <span style={{ marginLeft: 12 }}>
                          · Last run: {new Date(session.last_run_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={session.status} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                  <Stat label="Starting Capital" value={fmtCurrency(session.starting_capital)} />
                  <Stat label="Current Value" value={fmtCurrency(session.current_value || session.starting_capital)} />
                  <Stat
                    label="Total Return"
                    value={returnPct != null ? fmtPct(returnPct) : '—'}
                    color={returnPct != null ? (isPositive ? '#2fd6c3' : '#f85149') : undefined}
                  />
                  <Stat label="Symbol" value={`${session.symbol} (${session.asset_type})`} />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                  {session.status === 'active' && (
                    <SmallBtn
                      onClick={() => handlePause(session)}
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinner size={12} /> : '⏸ Pause'}
                    </SmallBtn>
                  )}
                  {session.status === 'paused' && (
                    <SmallBtn
                      onClick={() => handleResume(session)}
                      disabled={isLoading}
                      style={{ borderColor: 'var(--teal)', color: 'var(--teal)' }}
                    >
                      {isLoading ? <Spinner size={12} /> : '▶ Resume'}
                    </SmallBtn>
                  )}
                  <SmallBtn onClick={() => navigate(`/paper-trading/${session.id}`)}>
                    View Dashboard →
                  </SmallBtn>
                  <SmallBtn
                    onClick={() => setConfirmDelete(session)}
                    disabled={isLoading}
                    style={{ marginLeft: 'auto', color: '#f85149', borderColor: 'rgba(248,81,73,0.4)' }}
                  >
                    🗑 Delete
                  </SmallBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

function SmallBtn({ children, onClick, disabled, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)',
        padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
